// services/school-ui/src/pages/chat/attachments/authDownload.ts
//
// Bridge for Nova OS file downloads that require a Bearer JWT. The
// browser's default link-click flow sends no Authorization header, so
// clicking a `<a href="/uploads/foo.docx">` link rendered into chat
// markdown gets a 401 from Nova OS. We intercept those clicks, fetch
// the URL with the school-ui's auth header, and trigger a programmatic
// Blob download with the original filename.
//
// Scope: this helper recognizes the two Nova OS endpoints that emit
// download_url-style links into chat content today:
//   - /uploads/<filename>           (filesystem skill writes — see
//                                    nova-os/internal/skills/filesystem.go)
//   - /v1/conversations/<cid>/attachments/<aid>/download
//                                   (per-conversation attachments)
// Either path may appear in markdown as a relative URL ("/uploads/x.docx")
// or absolute against the Nova OS public base URL.

/** Heuristic: does this URL point at a Nova OS auth-required file path
 *  we should fetch with the JWT instead of letting the browser navigate?
 *  Accepts both relative ("/uploads/x") and absolute ("https://nova.example/uploads/x"). */
export function isAuthFetchable(url: string): boolean {
  try {
    const u = url.startsWith("http")
      ? new URL(url)
      : new URL(url, window.location.origin);
    if (u.pathname.startsWith("/uploads/")) return true;
    if (
      /^\/v1\/conversations\/[^/]+\/attachments\/[^/]+\/download$/.test(
        u.pathname,
      )
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Fetch a Nova OS file URL with the bearer JWT, then drive a browser
 *  download via a Blob URL. Filename precedence:
 *    1. Content-Disposition response header (RFC 5987 + plain)
 *    2. Caller-supplied `suggestedName`
 *    3. Last URL path segment, percent-decoded
 *  Throws on non-2xx response so callers can surface the error. */
export async function downloadWithAuth(
  url: string,
  authHeader: Record<string, string>,
  suggestedName?: string,
): Promise<void> {
  const r = await fetch(url, { headers: authHeader });
  if (!r.ok) {
    throw new Error(`download failed: ${r.status} ${r.statusText}`);
  }
  const blob = await r.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download =
      filenameFromContentDisposition(r.headers.get("Content-Disposition")) ??
      suggestedName ??
      filenameFromUrl(url);
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Defer revocation slightly so the click→download path can read the
    // blob in browsers that flush asynchronously.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

/** Parse a Content-Disposition header for a filename. Handles RFC 5987
 *  `filename*=UTF-8''<encoded>` and the simpler `filename="<name>"` and
 *  `filename=<name>` variants. */
function filenameFromContentDisposition(
  header: string | null,
): string | undefined {
  if (!header) return undefined;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/i.exec(header);
  const starValue = star?.[1];
  if (starValue) {
    try {
      return decodeURIComponent(starValue);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*"?([^"';]+)"?/i.exec(header);
  const plainValue = plain?.[1];
  if (plainValue) return plainValue.trim();
  return undefined;
}

function filenameFromUrl(url: string): string {
  try {
    const u = url.startsWith("http")
      ? new URL(url)
      : new URL(url, window.location.origin);
    const last = u.pathname.split("/").filter(Boolean).pop() ?? "download";
    return decodeURIComponent(last);
  } catch {
    return "download";
  }
}


/** 浏览器能直接渲染的产物类型。HTML 报告、PDF、图片、纯文本可以预览;
 *  Office 格式(docx/pptx/xlsx)浏览器渲染不了,硬"预览"只会得到乱码或
 *  一次隐式下载,所以那些仍然走下载。 */
export function isBrowserPreviewable(url: string): boolean {
  try {
    const u = url.startsWith("http") ? new URL(url) : new URL(url, window.location.origin);
    const ext = decodeURIComponent(u.pathname).split(".").pop()?.toLowerCase() ?? "";
    return ["html", "htm", "pdf", "png", "jpg", "jpeg", "gif", "webp", "svg", "txt", "md", "csv"].includes(ext);
  } catch {
    return false;
  }
}

/** 在新标签页里**预览**一份产物,而不是直接下载。
 *
 *  产物大多是 HTML 报告/试卷,浏览器能直接渲染;直接触发下载等于逼用户
 *  先存盘再打开(Jarod 2026-09-02)。预览页里浏览器自带下载/打印,所以
 *  "预览优先"不损失任何能力。
 *
 *  失败必须可见:链接指向的产物有时**根本没生成**(技能报告 skill_completed
 *  却没有产出任何文件,模型仍在回答里编了个文件名),旧行为是 console.error
 *  然后界面毫无反应,用户只知道"点了没用"。这里把错误抛出去,由调用方显示。 */
export async function previewWithAuth(
  url: string,
  authHeader: Record<string, string>,
): Promise<void> {
  const res = await fetch(url, { headers: authHeader });
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error("这份产物没有生成成功,文件不存在。请让助教重新生成一次。");
    }
    throw new Error(`打开失败 (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  // 不加 "noopener":按规范带 noopener 时 window.open 一律返回 null,
  // 于是标签页明明开了,这里却报「浏览器拦截了新标签页」(Jarod 2026-09-02)。
  // blob: URL 是我们自己同源生成的,没有 opener 泄露风险;去掉后 null 才真的
  // 表示被弹窗拦截。
  const win = window.open(objectUrl, "_blank");
  if (!win) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("浏览器拦截了新标签页,请允许弹出窗口后重试。");
  }
  // 给新标签页留出加载时间再回收;过早 revoke 会让页面空白。
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
